import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const LoaderV2 = ({ success = false, onAnimationComplete }) => {
    const [stage, setStage] = useState('loading');

    useEffect(() => {
        if (success && stage === 'loading') {
            setStage('resolving-a');
        }
    }, [success, stage]);

    useEffect(() => {
        let timeout;
        if (stage === 'resolving-a') {
            // Wait for spinner to fill the circle
            timeout = setTimeout(() => setStage('drawing-i'), 600);
        } else if (stage === 'drawing-i') {
            // Wait for 'i' stem to draw
            timeout = setTimeout(() => setStage('showing-i-dot'), 400);
        } else if (stage === 'showing-i-dot') {
            // Wait for 'i' dot, then show period
            timeout = setTimeout(() => setStage('showing-period'), 300);
        } else if (stage === 'showing-period') {
            timeout = setTimeout(() => {
                setStage('complete');
                onAnimationComplete?.();
            }, 400);
        }
        return () => clearTimeout(timeout);
    }, [stage, onAnimationComplete]);

    // Colors
    const baseColor = '#0C0A33';
    const accentColor = '#5C33FF';
    const INACTIVE_OPACITY = 0.1;
    const ACTIVE_OPACITY = 0.6;

    // Variants for the spinner
    const spinnerVariants = {
        animate: {
            rotate: 360,
            transition: {
                repeat: Infinity,
                duration: 1.2,
                ease: "linear",
            }
        }
    };

    // Circumference for filling animation
    const A_CIRCUMFERENCE = 102; // 2 * PI * 16.25 ≈ 102

    return (
        <div className="flex items-center justify-center w-full h-full min-h-[120px]">
            <div className="relative w-[100px] h-[91px]">
                <svg
                    width="100"
                    height="91"
                    viewBox="0 0 100 91"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-full h-full"
                    shapeRendering="geometricPrecision"
                >
                    {/* ================= BACKGROUND LAYER ================= */}
                    <path
                        d="M49 31C61.1503 31 71 40.8497 71 53C71 53.4549 70.9842 53.9064 70.957 54.3545C70.9843 54.5659 71 54.7812 71 55V70C71 72.7614 68.7614 75 66 75C63.6967 75 61.7579 73.4424 61.1777 71.3232C57.6906 73.6454 53.5038 75 49 75C36.8497 75 27 65.1503 27 53C27 40.8497 36.8497 31 49 31ZM49 42.5117C43.2074 42.5117 38.5117 47.2074 38.5117 53C38.5117 58.7926 43.2074 63.4883 49 63.4883C54.7926 63.4883 59.4883 58.7926 59.4883 53C59.4883 47.2074 54.7926 42.5117 49 42.5117Z"
                        fill={baseColor}
                        fillOpacity={INACTIVE_OPACITY}
                    />
                    <rect x="73" y="31" width="10" height="44" rx="5" fill={baseColor} fillOpacity={INACTIVE_OPACITY} />
                    <rect x="71" y="16" width="13" height="13" rx="6.5" fill={baseColor} fillOpacity={INACTIVE_OPACITY} />
                    <rect x="16" y="61" width="14" height="14" rx="7" fill={baseColor} fillOpacity={INACTIVE_OPACITY} />

                    {/* ================= ACTIVE / ANIMATED LAYERS ================= */}

                    {/* Mask using the 'a' shape. userSpaceOnUse fixes many coordinate glitches. */}
                    <defs>
                        <mask id="bowl-mask-v4" maskUnits="userSpaceOnUse">
                            <path
                                d="M49 31C61.1503 31 71 40.8497 71 53C71 53.4549 70.9842 53.9064 70.957 54.3545C70.9843 54.5659 71 54.7812 71 55V70C71 72.7614 68.7614 75 66 75C63.6967 75 61.7579 73.4424 61.1777 71.3232C57.6906 73.6454 53.5038 75 49 75C36.8497 75 27 65.1503 27 53C27 40.8497 36.8497 31 49 31ZM49 42.5117C43.2074 42.5117 38.5117 47.2074 38.5117 53C38.5117 58.7926 43.2074 63.4883 49 63.4883C54.7926 63.4883 59.4883 58.7926 59.4883 53C59.4883 47.2074 54.7926 42.5117 49 42.5117Z"
                                fill="white"
                            />
                        </mask>
                    </defs>

                    {/* 1. Rotating 'a' (Loading State) 
                 Using a solid stroke circle, masked. 
                 Optimized strokeWidth to 12.5px (matching channel width) to preserve rounded caps.
          */}
                    <AnimatePresence>
                        {(stage === 'loading' || stage === 'resolving-a') && (
                            <g mask="url(#bowl-mask-v4)">
                                <motion.circle
                                    cx="49"
                                    cy="53"
                                    r="16.25"
                                    stroke={baseColor}
                                    strokeOpacity={ACTIVE_OPACITY}
                                    strokeWidth="12.5"
                                    strokeLinecap="round"
                                    fill="none"
                                    style={{ willChange: "transform", transformOrigin: "49px 53px" }}
                                    variants={spinnerVariants}
                                    animate="animate"
                                    exit={{ opacity: 0 }}
                                    // Animate strokeDasharray: partial during loading, full during resolving-a
                                    initial={{ strokeDasharray: "30 72" }}
                                    transition={{
                                        strokeDasharray: { duration: 0.5, ease: "easeInOut" }
                                    }}
                                    // Use motion value for strokeDasharray based on stage
                                    strokeDasharray={stage === 'resolving-a' ? `${A_CIRCUMFERENCE} 0` : "30 72"}
                                />
                            </g>
                        )}
                    </AnimatePresence>

                    {/* 2. Solid 'a' (Success State) - Only appears AFTER spinner finishes */}
                    <motion.path
                        d="M49 31C61.1503 31 71 40.8497 71 53C71 53.4549 70.9842 53.9064 70.957 54.3545C70.9843 54.5659 71 54.7812 71 55V70C71 72.7614 68.7614 75 66 75C63.6967 75 61.7579 73.4424 61.1777 71.3232C57.6906 73.6454 53.5038 75 49 75C36.8497 75 27 65.1503 27 53C27 40.8497 36.8497 31 49 31ZM49 42.5117C43.2074 42.5117 38.5117 47.2074 38.5117 53C38.5117 58.7926 43.2074 63.4883 49 63.4883C54.7926 63.4883 59.4883 58.7926 59.4883 53C59.4883 47.2074 54.7926 42.5117 49 42.5117Z"
                        fill={baseColor}
                        initial={{ opacity: 0 }}
                        animate={{
                            opacity: (['drawing-i', 'showing-i-dot', 'showing-period', 'complete'].includes(stage)) ? ACTIVE_OPACITY : 0
                        }}
                        transition={{ duration: 0.3 }}
                    />

                    {/* 3. 'i' Stem - DRAWS from bottom to top */}
                    {(['drawing-i', 'showing-i-dot', 'showing-period', 'complete'].includes(stage)) && (
                        <motion.line
                            x1="78" y1="70"
                            x2="78" y2="36"
                            stroke={baseColor}
                            strokeOpacity={ACTIVE_OPACITY}
                            strokeWidth="10"
                            strokeLinecap="round"
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1 }}
                            transition={{ duration: 0.4, ease: "easeOut" }}
                        />
                    )}

                    {/* 4. 'i' Top Dot */}
                    <motion.rect
                        x="71" y="16" width="13" height="13" rx="6.5"
                        fill={baseColor}
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{
                            opacity: (['showing-i-dot', 'showing-period', 'complete'].includes(stage)) ? ACTIVE_OPACITY : 0,
                            scale: (['showing-i-dot', 'showing-period', 'complete'].includes(stage)) ? 1 : 0.5
                        }}
                        transition={{ duration: 0.25 }}
                    />

                    {/* 5. Period Dot (Purple) */}
                    <motion.rect
                        x="16" y="61" width="14" height="14" rx="7"
                        fill={accentColor}
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{
                            opacity: (['showing-period', 'complete'].includes(stage)) ? 1 : 0,
                            scale: (['showing-period', 'complete'].includes(stage)) ? 1 : 0.5
                        }}
                        transition={{ duration: 0.3 }}
                    />
                </svg>
            </div>
        </div>
    );
};

export default LoaderV2;
